"use client";
import Google from "@/components/SocialButton/Google";
import ThemeRegistry from "@/components/Theme/ThemeRegistry";
import Button, { BtnType } from "@/components/common/buttons/Button";
import InputTextfield from "@/components/common/inputs/InputTextfield";
import { ACCESS_TOKEN, ROLE } from "@/constants/common";
import { APP_ROUTES } from "@/constants/routes";
import { setEmail, updateUser } from "@/lib/features/authSlice";
import { setErrorMess, setLoading } from "@/lib/features/statusSlice";
import { useAppDispatch } from "@/lib/hooks";
import { cn } from "@/lib/utils";
import UserServices from "@/services/main/UserServices";
import { setCookieSSR } from "@/utils/auth";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Divider, Typography } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocale, useTranslations } from "next-intl";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import classes from "./styles.module.scss";
import MessageErrors from "@/components/admin/errors/MessageErrors";

export interface LoginForm {
  email: string;
  password?: string | null | undefined;
}

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
};

export default function Login() {
  const searchParams = useSearchParams();
  const redirect = searchParams.get("redirect");
  const download = searchParams.get("download") === "true";
  const locale = useLocale();
  const t = useTranslations("messages");
  const [isPasswordVisible, setIsPasswordVisible] = useState<boolean>(true);
  const dispatch = useAppDispatch();
  const [errorSubmit, setErrorSubmit] = useState<{
    show: boolean;
    messages: string;
  }>({
    show: false,
    messages: t("login_invalid_error"),
  });
  const router = useRouter();
  const schema = useMemo(
    () =>
      yup.object().shape({
        email: yup
          .string()
          .trim()
          .email(() => t("field_email_vali"))
          .required(() => t("field_email_vali_required")),
        // .test("is-business-email", () => t("field_email_vali_business"), (value) => {
        //   const domainSplit = value.split("@")[1];
        //   return !value || !freeEmailDomains.some((domain) => domain === domainSplit);
        // }),
        password: !isPasswordVisible
          ? yup
              .string()
              .trim()
              .required(() => t("field_password_vali_required"))
          : yup.string().trim().notRequired(),
      }),
    [t, isPasswordVisible],
  );
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
    clearErrors,
  } = useForm<LoginForm>({
    resolver: yupResolver(schema),
    mode: "onChange",
    shouldUnregister: true,
  });
  const queryClient = useQueryClient();

  const sendVerifyEmailMutation = useMutation({
    mutationFn: async (email: string) => {
      if (!email || !email.includes("@")) throw new Error("Invalid email");
      return await UserServices.resendOtpVerify({ email: email });
    },
    onSuccess: (data: any) => {
      router.push(
        `${APP_ROUTES(locale).VERIFIED_OTP.to}?email=${encodeURIComponent(data?.email)}${
          redirect ? `&redirect=${encodeURIComponent(redirect)}` : ""
        }`,
      );
    },
    onError: (e: any) => {
      dispatch(setErrorMess(e?.detail || t("common_something_went_wrong")));
    },
  });

  const loginMutation = useMutation({
    mutationKey: ["login"],
    mutationFn: async (data: LoginForm) => {
      return await UserServices.login(data);
    },
    onSuccess: (res: any) => {
      if (res?.is2FA || res?.isLogin) {
        dispatch(setLoading(false));
        dispatch(setEmail(res?.email));
        router.push(
          `${APP_ROUTES(locale).VERIFIED_OTP_INDENTITY.to}?email=${encodeURIComponent(res?.email)}${
            redirect ? `&redirect=${encodeURIComponent(redirect)}` : ""
          }`,
        );
        return;
      }
      dispatch(updateUser(res?.user));
      setCookieSSR(ACCESS_TOKEN, res?.token);
      setCookieSSR(ROLE, res?.user?.admin_type);
      queryClient.clear();
      if (redirect) {
        window.location.href = decodeURIComponent(redirect);
      } else if (res?.user) {
        window.location.href = APP_ROUTES(locale).HOME.to;
      }
    },
    onError: (error: any) => {
      if (error?.detail === "notVerified") {
        const email = error?.email || loginMutation.variables?.email;
        if (email) {
          sendVerifyEmailMutation.mutate(email);
        } else {
          setErrorSubmit((prev) => ({
            ...prev,
            show: true,
          }));
        }
      } else {
        setErrorSubmit({
          show: true,
          messages: error?.detail || t("login_invalid_error"),
        });
      }
      dispatch(setLoading(false));
    },
  });

  const onSubmit = async (data: LoginForm) => {
    setErrorSubmit((prev) => ({
      ...prev,
      show: false,
    }));
    const { data: checkEmailBlocked } = await UserServices.checkEmailBlocked(
      data.email,
    ).catch(() => ({ isBlocked: false }));
    if (checkEmailBlocked?.isBlocked) {
      setError("email", {
        type: "manual",
        message: t("field_email_blocked_login"),
      });
      return;
    }

    loginMutation.mutate(data);
  };


  return (
    <ThemeRegistry>
      <Box className="flex flex-col w-full h-full">
        <form
          onSubmit={handleSubmit(onSubmit)}
          name="login"
          noValidate
          autoComplete="off"
        >
          <p
            className="text-cimigo-blue text-[22px] md:text-[24px] font-semibold leading-[133.333%] m-0"
            translation-key="login_title"
            translation-download-key="login_download_title"
          >
            {download ? (
              <>{t("login_download_title")}</>
            ) : (
              <>{t("login_title")}</>
            )}
          </p>
          <Box className="flex flex-col gap-4 mt-8">
            <Box>
              <InputTextfield
                title={t("field_email_business")}
                translation-key="field_email_business"
                name="email"
                placeholder={t("field_email_business_placeholder")}
                translation-key-placeholder="field_email_business_placeholder"
                type="text"
                inputRef={register("email")}
                errorMessage={errors.email?.message}
              />
            </Box>
            {!isPasswordVisible && (
              <Box className={cn("")}>
                <InputTextfield
                  title={t("field_password")}
                  translation-key="field_password"
                  name="password"
                  type="password"
                  showEyes
                  placeholder={t("field_password_placeholder")}
                  translation-key-placeholder="field_password_placeholder"
                  inputRef={register("password")}
                  errorMessage={errors.password?.message}
                />
              </Box>
            )}
          </Box>
          {errorSubmit.show && (
            <MessageErrors>{errorSubmit.messages}</MessageErrors>
          )}
          <Box className="flex justify-between items-center mt-6">
            <button
              type={"button"}
              onClick={() => {
                setIsPasswordVisible(!isPasswordVisible);
              }}
              className="underline cursor-pointer text-[14px] font-medium p-0 border-none bg-transparent text-cimigo-blue forgot-password-link hover:no-underline"
              translation-key="login_have_password"
            >
              {t("login_have_password")}
            </button>
            <Box>
              <Link
                href={APP_ROUTES(locale).FORGOTPASSWORD.to}
                className="underline text-[14px] font-medium text-cimigo-blue forgot-password-link hover:no-underline"
                translation-key="login_redirect_forgot_password"
              >
                {t("login_redirect_forgot_password")}
              </Link>
            </Box>
          </Box>
          <Button
            btnType={BtnType.Raised}
            sx={{ my: "24px" }}
            type="submit"
            loading={loginMutation.isPending}
            className={cn(classes.btnAuthForm, "login-submit-button")}
          >
            <p
              className="m-0 !tracking-widest"
              translation-key="login_btn_login"
            >
              {t("login_btn_login")}
            </p>
          </Button>
          <Divider sx={{ margin: "0px 0px 24px 0px" }}>
            <Typography
              sx={{
                color: "--eerie-black-65",
                fontFamily: "Montserrat",
                lineHeight: "24px",
                fontSize: "14px",
                fontWeight: 400,
                letterSpacing: "0.25px",
              }}
              translation-key="login_login_with"
            >
              {t("login_login_with")}
            </Typography>
          </Divider>
          <Box className="flex flex-col gap-[16px]">
            <Google>Google</Google>
            {/* <Microsoft>Microsoft</Microsoft> */}
          </Box>
          <Box
            className="text-[14px] flex flex-row justify-center mt-6"
            sx={{ textTransform: "none" }}
          >
            <p
              className="m-0 text-sm/6  text-gray-90 tracking-[0.25px] font-medium"
              translation-key="login_do_not_have_account"
            >
              {t("login_do_not_have_account")}
              <Link
                className="underline text-sm/6 pl-1 hover:no-underline  text-cimigo-blue login-page-register-now"
                href={`${APP_ROUTES(locale).REGISTER.to}${redirect ? `?redirect=${encodeURIComponent(redirect)}` : ""}`}
                translation-key="login_page_register_now"
              >
                {t("login_page_register_now")}
              </Link>
            </p>
          </Box>
        </form>
      </Box>
    </ThemeRegistry>
  );
}
